import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { BRAND, LOGOS, API } from "@/brand";
import Crown from "@/components/Crown";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("ENTER A VALID EMAIL");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("YOU'RE IN THE BROTHERHOOD");
      setEmail("");
    } catch {
      toast.error("SOMETHING BROKE — TRY AGAIN");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer-brand-section" className="relative overflow-hidden border-t border-zinc-800/70 bg-[#080809] concrete-grain">
      <Crown className="absolute -top-6 right-[6%] h-32 w-48 text-zinc-900/80 rotate-6" />

      <div className="mx-auto max-w-7xl px-4 sm:px-8 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-24">
          <div>
            <img
              src={LOGOS.emblem}
              alt={BRAND.name}
              className="logo-blend w-40 mb-6"
            />
            <p className="max-w-sm text-xs leading-loose text-zinc-500">
              Premium Southern California streetwear. Aggressive luxury, Chicano lettering, raw graffiti — clean enough
              for the embroidery machine, mean enough for the streets.
            </p>
            <a
              data-testid="footer-instagram-link"
              href={BRAND.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 border border-zinc-700/70 px-5 py-2.5 text-[10px] tracking-[0.3em] text-zinc-300 uppercase hover:border-zinc-400 hover:text-zinc-100 transition-colors duration-300"
            >
              <Instagram className="h-3.5 w-3.5" />
              {BRAND.instagramHandle}
            </a>
          </div>

          <div>
            <p className="text-[10px] tracking-[0.45em] text-zinc-500 uppercase mb-2">The Wire</p>
            <h3 className="font-display text-3xl sm:text-4xl chrome-text-bright uppercase mb-6">
              Exclusive early access
            </h3>
            <form onSubmit={subscribe} className="flex max-w-md" data-testid="footer-newsletter-form">
              <input
                data-testid="footer-newsletter-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="YOUR EMAIL"
                className="flex-1 border border-zinc-700 bg-zinc-950 px-4 py-3.5 text-xs tracking-[0.2em] text-zinc-200 placeholder:text-zinc-600 focus:border-zinc-400 focus:outline-none uppercase"
              />
              <button
                data-testid="footer-newsletter-submit"
                type="submit"
                disabled={submitting}
                className="border border-zinc-300 bg-zinc-100 px-6 text-[10px] font-bold tracking-[0.3em] text-zinc-950 uppercase hover:bg-transparent hover:text-zinc-100 transition-colors duration-300 disabled:opacity-50"
              >
                {submitting ? "…" : "Lock in"}
              </button>
            </form>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 60 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="px-2 text-center select-none"
        data-testid="footer-giant-wordmark"
      >
        <img
          src={LOGOS.wordmark}
          alt={BRAND.name}
          className="logo-blend logo-fade mx-auto w-full max-w-6xl"
        />
      </motion.div>

      <div className="border-t border-zinc-900 mt-10">
        <div className="mx-auto flex max-w-7xl flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-8 py-6">
          <p className="text-[9px] tracking-[0.3em] text-zinc-600 uppercase">
            © 2026 {BRAND.name} — All Rights Reserved
          </p>
          <p className="text-[9px] tracking-[0.3em] text-zinc-700 uppercase">{BRAND.short}</p>
        </div>
      </div>
    </footer>
  );
}
